import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Invitation } from '../types';

const InvitationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('pending');

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/invitations');
      setInvitations(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load invitations:', err);

      // Only set error for actual failures, not empty results
      if (err.response) {
        // Server responded with error status (4xx, 5xx)
        setError(err.response.data?.error?.message || 'Failed to load invitations');
      } else if (err.request) {
        // Network error - no response received
        setError('Network error. Please check your connection.');
      } else {
        // Other errors
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId: string) => {
    try {
      await apiClient.post(`/v1/invitations/${invitationId}/accept`);
      loadInvitations();
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      alert(err.response?.data?.error?.message || 'Failed to accept invitation');
    }
  };

  const handleReject = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to reject this invitation?')) {
      return;
    }

    try {
      await apiClient.post(`/v1/invitations/${invitationId}/reject`);
      loadInvitations();
    } catch (err: any) {
      console.error('Failed to reject invitation:', err);
      alert(err.response?.data?.error?.message || 'Failed to reject invitation');
    }
  };

  const filteredInvitations = invitations.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <h1>
            <i className="fas fa-envelope me-2"></i>
            Invitations
          </h1>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
              >
                <i className="fas fa-clock me-1"></i>
                Pending
                {invitations.filter(i => i.status === 'pending').length > 0 && (
                  <span className="badge bg-warning ms-2">
                    {invitations.filter(i => i.status === 'pending').length}
                  </span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'accepted' ? 'active' : ''}`}
                onClick={() => setFilter('accepted')}
              >
                <i className="fas fa-check me-1"></i>
                Accepted
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'rejected' ? 'active' : ''}`}
                onClick={() => setFilter('rejected')}
              >
                <i className="fas fa-times me-1"></i>
                Rejected
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                <i className="fas fa-list me-1"></i>
                All
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Invitations List */}
      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading invitations...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          ) : filteredInvitations.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <h5>No Invitations</h5>
                <p className="text-muted">
                  {filter === 'pending'
                    ? "You don't have any pending invitations."
                    : `No ${filter} invitations found.`}
                </p>
              </div>
            </div>
          ) : (
            <div className="list-group">
              {filteredInvitations.map((invitation) => (
                <InvitationCard
                  key={invitation.invitation_id}
                  invitation={invitation}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onNavigate={navigate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface InvitationCardProps {
  invitation: Invitation;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onNavigate: (path: string) => void;
}

const InvitationCard: React.FC<InvitationCardProps> = ({
  invitation,
  onAccept,
  onReject,
  onNavigate
}) => {
  const getInvitationIcon = () => {
    switch (invitation.type) {
      case 'co_organizer':
        return 'fa-calendar';
      case 'session':
        return 'fa-microphone';
      case 'admin_role':
        return 'fa-crown';
      default:
        return 'fa-envelope';
    }
  };

  const getInvitationTitle = () => {
    switch (invitation.type) {
      case 'co_organizer':
        return 'Co-Organizer Invitation';
      case 'session':
        return 'Session Invitation';
      case 'admin_role':
        return 'Admin Role Invitation';
      default:
        return 'Invitation';
    }
  };

  const getInvitationDescription = () => {
    switch (invitation.type) {
      case 'co_organizer':
        return `${invitation.inviter_name || 'Someone'} invited you to be a co-organizer for ${invitation.target_name || 'an event'}`;
      case 'session':
        return `${invitation.inviter_name || 'Someone'} invited you to present a session at ${invitation.target_name || 'an event'}`;
      case 'admin_role':
        return `${invitation.inviter_name || 'An admin'} invited you to become an administrator`;
      default:
        return 'You have received an invitation';
    }
  };

  const getStatusBadge = () => {
    switch (invitation.status) {
      case 'pending':
        return <span className="badge bg-warning">Pending</span>;
      case 'accepted':
        return <span className="badge bg-success">Accepted</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      default:
        return null;
    }
  };

  const handleViewTarget = () => {
    if (invitation.type === 'co_organizer' && invitation.target_id) {
      onNavigate(`/events/${invitation.target_id}`);
    }
  };

  return (
    <div className="list-group-item">
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-2">
            <i className={`fas ${getInvitationIcon()} fa-2x text-primary me-3`}></i>
            <div>
              <h6 className="mb-0">{getInvitationTitle()}</h6>
              <small className="text-muted">
                {new Date(invitation.created_at).toLocaleDateString()}
              </small>
            </div>
          </div>
          <p className="mb-2">{getInvitationDescription()}</p>
          {invitation.status === 'pending' && invitation.expires_at && (
            <small className="text-muted">
              <i className="fas fa-clock me-1"></i>
              Expires: {new Date(invitation.expires_at).toLocaleDateString()}
            </small>
          )}
        </div>
        <div className="ms-3">
          {getStatusBadge()}
        </div>
      </div>

      {invitation.status === 'pending' && (
        <div className="mt-3 d-flex gap-2">
          {invitation.type === 'session' ? (
            <button
              className="btn btn-sm btn-success"
              onClick={() => onNavigate(`/invitations/${invitation.invitation_id}/accept`)}
            >
              <i className="fas fa-check me-1"></i>
              Accept & Select Talk
            </button>
          ) : (
            <button
              className="btn btn-sm btn-success"
              onClick={() => onAccept(invitation.invitation_id)}
            >
              <i className="fas fa-check me-1"></i>
              Accept
            </button>
          )}
          <button
            className="btn btn-sm btn-danger"
            onClick={() => onReject(invitation.invitation_id)}
          >
            <i className="fas fa-times me-1"></i>
            Reject
          </button>
          {invitation.type === 'co_organizer' && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={handleViewTarget}
            >
              <i className="fas fa-eye me-1"></i>
              View Event
            </button>
          )}
        </div>
      )}

      {invitation.status === 'accepted' && invitation.type === 'co_organizer' && (
        <div className="mt-3">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={handleViewTarget}
          >
            <i className="fas fa-eye me-1"></i>
            View Event
          </button>
        </div>
      )}
    </div>
  );
};

export default InvitationsPage;
